/**
 * WelcomeSection Component
 *
 * Welcome area with date/time, personalized greeting, and Quick Log Entry CTA.
 * Based on DASHBOARD_FUNCTIONAL_SPEC.md Section 3.
 */

import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

type CaseFilePermission = 'owner' | 'editor' | 'logger' | 'viewer';

/** Returns true if the permission level allows write actions (create care logs) */
function hasLogAccess(permission?: CaseFilePermission): boolean {
  // logger permission allows creating care logs
  if (!permission) return true; // Default to showing button if no CaseFile selected
  return permission === 'owner' || permission === 'editor' || permission === 'logger';
}

interface WelcomeSectionProps {
  careRecipientName?: string;
  userPermission?: CaseFilePermission;
  onQuickLogClick?: () => void;
}

export function WelcomeSection({
  careRecipientName,
  userPermission,
  onQuickLogClick,
}: WelcomeSectionProps): JSX.Element {
  const { user } = useAuth();
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Extract first name from display name or email
  const firstName = user?.displayName?.split(' ')[0]
    || user?.email?.split('@')[0]
    || 'there';

  // Format date: "Sunday, January 4, 2026 • 2:14 PM"
  const formattedDate = currentDateTime.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const formattedTime = currentDateTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  return (
    <div className="bg-gradient-to-br from-evergreen/5 to-evergreen/10 rounded-xl p-6 lg:p-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="space-y-2">
          {/* Date and Time */}
          <p className="text-sm text-muted">
            {formattedDate} • {formattedTime}
          </p>

          {/* Greeting */}
          <h1 className="font-display text-2xl lg:text-3xl text-deep-forest font-light">
            Welcome back, {firstName}
          </h1>

          {/* Context */}
          <p className="text-slate-ink">
            {careRecipientName
              ? `Here's what needs your attention for ${careRecipientName}'s care today.`
              : "Here's what needs your attention today."
            }
          </p>
        </div>

        {/* Quick Log Entry Button (only for users with log access) */}
        {hasLogAccess(userPermission) && (
          <div className="flex-shrink-0">
            <button
              onClick={onQuickLogClick}
              className="btn-primary btn-lg flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Quick Log Entry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default WelcomeSection;
